import { useState, useRef, useMemo, useCallback } from 'react';
import { Checkbox, Menu, Tag, Tooltip } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { DownOutlined } from '@ant-design/icons';
import { truncateByLength } from '../common/helper';
import React from 'react';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import SearchInput from './SearchInput';
import './style/TeamsSelector.css';
import type { TeamGroup } from '@/apis/teamGroups';
import {
  groupMatchesSearch,
  groupSelectionState,
  teamPickerSectionLabels,
  toggleGroupSelection,
} from '@/common/utils/teamGroups';

export interface Team {
  id: string;
  name: string;
  color?: string;
  permissions?: {
    is_create?: boolean;
    is_update?: boolean;
  };
  disabled?: boolean;
}

interface TeamsSelectorProps<T extends Team> {
  teamsOptions: T[];
  selectedTeams: T[];
  /** Called with option objects from ``teamsOptions`` (or kept selected ones). */
  onChange: (teams: T[]) => void;
  singleSelect?: boolean;
  formState?: string;
  hideChevronWhenDisabled?: boolean;
  disabled?: boolean;
  onSearch?: (value: string) => void;
  teamsSearch?: string;
  /** Defaults to true so every team picker gets a search box like products. */
  showSearch?: boolean;
  /**
   * Opt-in team groups (team filters only). Shown above the teams; picking one
   * adds its teams from ``teamsOptions``, picking it again removes them.
   * Groups themselves are never added to the selection.
   */
  teamGroups?: TeamGroup[];
}

/** Options with ``disabled`` set when the form state lacks permission. */
const withTeamPermissions = <T extends Team>(
  teamsOptions: T[],
  formState: string
): T[] =>
  teamsOptions.map((team) => {
    const hasPermission =
      formState === 'add'
        ? team?.permissions?.is_create
        : formState === 'edit'
          ? team?.permissions?.is_update
          : true;

    return {
      ...team,
      disabled: team.disabled || !hasPermission,
    };
  });

/**
 * Teams selector dropdown component with multi/single select support
 *
 * Displays teams as a dropdown with colored indicators and permission-based filtering.
 * Supports both single and multi-select modes with tag-based selected team display.
 *
 * @param teamsOptions - Array of available teams to select from
 * @param selectedTeams - Currently selected teams
 * @param onChange - Callback function called when selection changes
 * @param singleSelect - If true, only allows selecting one team at a time
 * @param formState - Form context ('add'/'edit') used for permission filtering
 * @param disabled - If true, prevents the dropdown from opening and selection changes
 */
const TeamsSelector = <T extends Team>({
  teamsOptions,
  selectedTeams,
  onChange,
  hideChevronWhenDisabled = false,
  singleSelect = false,
  formState = '',
  disabled = false,
  onSearch,
  teamsSearch = '',
  showSearch = true,
  teamGroups,
}: TeamsSelectorProps<T>): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const [localSearch, setLocalSearch] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(
    undefined
  );
  const searchValue = onSearch ? teamsSearch : localSearch;

  const triggerClassName = [
    'teams-selector-trigger',
    disabled ? 'is-disabled' : '',
    hideChevronWhenDisabled ? 'is-chevron-hidden' : 'is-default',
  ]
    .filter(Boolean)
    .join(' ');

  const updateSearch = useCallback(
    (val: string) => {
      if (onSearch) {
        onSearch(val);
      } else {
        setLocalSearch(val);
      }
    },
    [onSearch]
  );

  const clearSearch = useCallback(() => {
    updateSearch('');
  }, [updateSearch]);

  const toggleOpen = React.useCallback(() => {
    if (!disabled) {
      const nextOpen = !open;
      setOpen(nextOpen);
      if (nextOpen && triggerRef.current) {
        setDropdownWidth(triggerRef.current.clientWidth);
      }
      if (!nextOpen && searchValue) {
        clearSearch();
      }
    }
  }, [disabled, open, searchValue, clearSearch]);

  const handleVisibleChange = React.useCallback(
    (visible: boolean) => {
      if (!disabled) {
        setOpen(visible);
        if (visible && triggerRef.current) {
          setDropdownWidth(triggerRef.current.clientWidth);
        }
        if (!visible && searchValue) {
          clearSearch();
        }
      }
    },
    [disabled, searchValue, clearSearch]
  );

  const handleSelect = (team: T): void => {
    if (singleSelect) {
      onChange([team]);
    } else if (!selectedTeams?.some((t) => t.id === team.id)) {
      onChange([...selectedTeams, team]);
    }
    setOpen(false);
  };

  const handleRemove = (teamId: string): void => {
    if (singleSelect) {
      onChange([]);
    } else {
      onChange(selectedTeams.filter((team) => team.id !== teamId));
    }
  };

  const availableTeams = useMemo(() => {
    const withPermissions = withTeamPermissions(teamsOptions, formState);
    if (onSearch) return withPermissions;
    const q = searchValue.trim().toLowerCase();
    if (!q) return withPermissions;
    return withPermissions.filter((team) =>
      team.name.toLowerCase().includes(q)
    );
  }, [teamsOptions, formState, onSearch, searchValue]);

  // Groups limited to teams the user can pick here (enabled options).
  const pickableGroups = useMemo(() => {
    if (!teamGroups?.length || singleSelect) return [];
    const pickable = new Set(
      withTeamPermissions(teamsOptions, formState)
        .filter((team) => !team.disabled)
        .map((team) => String(team.id))
    );
    return teamGroups
      .map((group) => ({
        ...group,
        team_ids: group.team_ids.filter((id) => pickable.has(id)),
      }))
      .filter(
        (group) =>
          group.team_ids.length > 0 &&
          groupMatchesSearch(
            group,
            searchValue,
            (id) => teamsOptions.find((team) => String(team.id) === id)?.name
          )
      );
  }, [teamGroups, singleSelect, teamsOptions, formState, searchValue]);

  const selectedIds = (selectedTeams ?? []).map((team) => String(team.id));

  const handleGroupToggle = (group: TeamGroup): void => {
    const nextIds = new Set(toggleGroupSelection(group, selectedIds));
    const kept = (selectedTeams ?? []).filter((team) =>
      nextIds.has(String(team.id))
    );
    const keptIds = new Set(kept.map((team) => String(team.id)));
    const added = teamsOptions.filter(
      (team) => nextIds.has(String(team.id)) && !keptIds.has(String(team.id))
    );
    onChange([...kept, ...added]);
  };

  const menu = (
    <div
      className='teams-selector-menu'
      // Runtime values (trigger width, team colour); not expressible in CSS.
      // eslint-disable-next-line react/forbid-dom-props
      style={{
        width: dropdownWidth ? `${dropdownWidth}px` : '100%',
      }}
    >
      {showSearch && (
        <div
          className='teams-selector-search-wrapper'
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <SearchInput
            placeholder={`Search ${themeT('team', theme.themeType).toLowerCase()}`}
            onChange={updateSearch}
            onClear={clearSearch}
            defaultValue={searchValue}
            width='100%'
          />
        </div>
      )}
      <Menu
        className={`teams-selector-inner-menu ${showSearch ? 'teams-selector-inner-menu-no-shadow' : ''}`}
      >
        {pickableGroups.length > 0 && (
          <Menu.ItemGroup
            key='team-groups'
            title={teamPickerSectionLabels().groups}
          >
            {pickableGroups.map((group) => {
              const state = groupSelectionState(group, selectedIds);
              return (
                <Menu.Item
                  key={`team-group:${group.id}`}
                  onClick={() => handleGroupToggle(group)}
                >
                  <div
                    className='teams-selector-option-wrapper'
                    // eslint-disable-next-line react/forbid-dom-props -- fits the trigger's measured width
                    style={{
                      maxWidth: dropdownWidth
                        ? `${dropdownWidth - 36}px`
                        : '100%',
                    }}
                  >
                    {/* Display only: the menu item handles the click. */}
                    <span className='teams-selector-group-checkbox'>
                      <Checkbox
                        // Still focusable, so it needs a name of its own.
                        aria-label={group.name}
                        checked={state.checked}
                        indeterminate={state.indeterminate}
                      />
                    </span>
                    {/* Wraps like the team names below (no truncation). */}
                    <span className='teams-selector-option-text'>
                      {`${group.name} (${group.team_ids.length})`}
                    </span>
                  </div>
                </Menu.Item>
              );
            })}
          </Menu.ItemGroup>
        )}
        {pickableGroups.length > 0 && (
          // Heads the normal teams so they don't read as groups.
          <Menu.ItemGroup
            key='teams-heading'
            title={teamPickerSectionLabels().teams}
          />
        )}
        {availableTeams.length > 0 ? (
          availableTeams.map((team) => (
            <Menu.Item
              key={team.id}
              onClick={() => handleSelect(team)}
              disabled={team.disabled}
            >
              <div
                className='teams-selector-option-wrapper'
                // Runtime values (trigger width, team colour); not expressible in CSS.
                // eslint-disable-next-line react/forbid-dom-props
                style={{
                  maxWidth: dropdownWidth ? `${dropdownWidth - 36}px` : '100%',
                }}
              >
                <span
                  className='teams-selector-option-indicator'
                  // Runtime values (trigger width, team colour); not expressible in CSS.
                  // eslint-disable-next-line react/forbid-dom-props
                  style={{
                    backgroundColor: team.color,
                  }}
                />
                <span className='teams-selector-option-text'>{team.name}</span>
              </div>
            </Menu.Item>
          ))
        ) : (
          <Menu.Item disabled className='teams-selector-empty-item'>
            {searchValue
              ? 'No matches found'
              : `You’ve selected all available ${themeT('team', theme.themeType).toLowerCase()}s`}
          </Menu.Item>
        )}
      </Menu>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      onVisibleChange={handleVisibleChange}
      visible={open}
      disabled={disabled}
    >
      <div ref={triggerRef} onClick={toggleOpen} className={triggerClassName}>
        {selectedTeams?.length === 0 && (
          <span className='teams-selector-placeholder'>Select...</span>
        )}

        {selectedTeams.map((team) => (
          <Tag
            key={team.id}
            closable={!disabled}
            onClose={(e) => {
              e?.stopPropagation();
              handleRemove(team.id);
            }}
            className='teams-selector-tag'
          >
            <span
              className='teams-selector-tag-indicator'
              // Runtime values (trigger width, team colour); not expressible in CSS.
              // eslint-disable-next-line react/forbid-dom-props
              style={{
                backgroundColor: team.color,
              }}
            ></span>
            <span className='teams-selector-tag-text'>
              <Tooltip title={team.name}>
                <span>{truncateByLength(team.name, 12)}</span>
              </Tooltip>
            </span>
          </Tag>
        ))}
        {!hideChevronWhenDisabled && (
          <DownOutlined className='teams-selector-arrow' />
        )}
      </div>
    </Dropdown>
  );
};

export default TeamsSelector;
